import type { ChangeEvent } from 'react'
import type { AccountSettingsForm } from './index.h'

import { api } from '@setplex/tria-api'
import { attach, combine, createEvent, createStore, sample } from 'effector'
import { createForm } from 'effector-forms'
import { or } from 'patronum'
import { t } from '~/core/i18n/i18next'
import { register } from '~/core/repository'
import {
  entitiesNotificationToastsModel,
  NotificationToastType,
} from '~/entities/notification-toasts'
import { model as session } from '~/entities/session'
import {
  $passwordI18n,
  FROM,
  passwordSecurity,
} from '~/features/password-security'
import {
  maxLength,
  minLength,
  password,
  required,
  username,
} from '~/shared/validators'
import { hasUsernameChanged, shouldValidatePasswords } from './lib'
import { $accountSettingsFormTranslations } from './translations'

// ============================================================================
// Events
// ============================================================================

export const init = createEvent()
export const left = createEvent()

const handleChangeUsername = createEvent<{
  '<event>': ChangeEvent<HTMLInputElement>
}>()

const handleChangeCurrentPassword = createEvent<{
  '<event>': ChangeEvent<HTMLInputElement>
}>()

const handleChangeNewPassword = createEvent<{
  '<event>': ChangeEvent<HTMLInputElement>
}>()

const handleChangeConfirmPassword = createEvent<{
  '<event>': ChangeEvent<HTMLInputElement>
}>()

const handleChangeLogoutSessions = createEvent<{
  '<event>': ChangeEvent<HTMLInputElement>
}>()

const handleBlurUsername = createEvent()
const handleBlurCurrentPassword = createEvent()
const handleBlurNewPassword = createEvent()
const handleBlurConfirmPassword = createEvent()

const handleSubmit = createEvent()

// ============================================================================
// API Effects
// ============================================================================

const updateUsernameFx = attach({
  effect: api.subscriber.updateSubscriberUsernameFx,
})

/**
 * Update subscriberName in local store without touching $session.
 * This avoids triggering other stores that depend on $session (profiles, versioning, SSE, etc.).
 */
sample({
  clock: updateUsernameFx.done,
  fn: ({ params }) => params.username,
  target: session.updateSubscriberName,
})

const updatePasswordFx = attach({
  effect: api.subscriber.updateSubscriberPasswordFx,
})

// ============================================================================
// Form Definition
// ============================================================================

/**
 * Store initial username for comparison
 */
const $initialUsername = session.$subscriberName.map((name) => name ?? '')

/**
 * Store for logout after password change checkbox
 */
const $logoutAfterPasswordChange = createStore<boolean>(false)

/**
 * Account settings form with validation rules
 */
const accountSettingsForm = createForm<AccountSettingsForm>({
  fields: {
    username: {
      init: '',
      rules: [required(), maxLength.validator(80), username()],
    },
    currentPassword: {
      init: '',
      rules: [
        {
          name: 'requiredIfPasswords',
          validator: (value, form: AccountSettingsForm) => {
            if (!shouldValidatePasswords(form)) return true
            return Boolean(value)
          },
        },
      ],
    },
    newPassword: {
      init: '',
      rules: [
        {
          name: 'requiredIfPasswords',
          validator: (value, form: AccountSettingsForm) => {
            if (!shouldValidatePasswords(form)) return true
            return Boolean(value)
          },
        },
        {
          name: maxLength.name,
          source: passwordSecurity.$policy,
          validator: (value, form: AccountSettingsForm, policy) => {
            const targetPolicy = policy[FROM.ACCOUNT]
            if (!shouldValidatePasswords(form)) return true
            return maxLength.validator(targetPolicy.max).validator(value)
          },
        },
        {
          name: password.name,
          source: passwordSecurity.$policy,
          validator: (value, form: AccountSettingsForm, policy) => {
            const targetPolicy = policy[FROM.ACCOUNT]
            if (!shouldValidatePasswords(form)) return true
            return password.validator(targetPolicy.regex).validator(value)
          },
        },
        {
          name: minLength.name,
          source: passwordSecurity.$policy,
          validator: (value, form: AccountSettingsForm, policy) => {
            const targetPolicy = policy[FROM.ACCOUNT]
            if (!shouldValidatePasswords(form)) return true
            return minLength.validator(targetPolicy.min).validator(value)
          },
        },
      ],
    },
    confirmPassword: {
      init: '',
      rules: [
        {
          name: 'requiredIfPasswords',
          validator: (value, form: AccountSettingsForm) => {
            if (!shouldValidatePasswords(form)) return true
            return Boolean(value)
          },
        },
        {
          name: 'equalTo',
          validator: (value, form: AccountSettingsForm) => {
            if (!shouldValidatePasswords(form)) return true
            return value === form.newPassword
          },
        },
      ],
    },
  },
  validateOn: ['submit', 'blur'],
})

/**
 * Check if all password fields are filled
 */
const $allPasswordsFilled = accountSettingsForm.$values.map(
  (values) =>
    Boolean(values.currentPassword) &&
    Boolean(values.newPassword) &&
    Boolean(values.confirmPassword)
)

/**
 * Username errors
 */
sample({
  clock: api.events.logic.usernameNotUnique,
  fn: () => ({ rule: 'unique' }),
  target: accountSettingsForm.fields.username.addError,
})

sample({
  clock: api.events.logic.usernameNotNotMatchRE,
  fn: () => ({ rule: 'username' }),
  target: accountSettingsForm.fields.username.addError,
})

sample({
  clock: api.events.logic.usernameTooLong,
  fn: () => ({ rule: 'maxLength' }),
  target: accountSettingsForm.fields.username.addError,
})

sample({
  clock: api.events.logic.subscriberUsernameRequired,
  fn: () => ({ rule: 'required' }),
  target: accountSettingsForm.fields.username.addError,
})

/**
 * Current password errors
 */
sample({
  clock: api.events.logic.subscriberPasswordRequired,
  fn: () => ({ rule: 'required' }),
  target: accountSettingsForm.fields.currentPassword.addError,
})

sample({
  clock: api.events.logic.subscriberPasswordWrong,
  fn: () => ({ rule: 'oldInvalid' }),
  target: accountSettingsForm.fields.currentPassword.addError,
})

/**
 * New password errors
 */
sample({
  clock: api.events.logic.subscriberCurrentNewPasswordEqual,
  fn: () => ({ rule: 'oldNewEqual' }),
  target: accountSettingsForm.fields.newPassword.addError,
})

sample({
  clock: api.events.logic.passwordTooShort,
  fn: () => ({ rule: 'password' }),
  target: accountSettingsForm.fields.newPassword.addError,
})

sample({
  clock: api.events.logic.passwordTooLong,
  fn: () => ({ rule: 'maxLength' }),
  target: accountSettingsForm.fields.newPassword.addError,
})

sample({
  clock: api.events.logic.passwordNotMatchRE,
  fn: () => ({ rule: 'password' }),
  target: accountSettingsForm.fields.newPassword.addError,
})

// ============================================================================
// Form Initialization
// ============================================================================

/**
 * Initialize form with username from session
 */
sample({
  clock: init,
  source: session.$subscriberName,
  fn: (subscriberName) => ({
    username: subscriberName ?? '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  }),
  target: accountSettingsForm.set,
})

/**
 * Reset logout checkbox when leaving page
 */
sample({
  clock: left,
  fn: () => false,
  target: $logoutAfterPasswordChange,
})

/**
 * Reset form when leaving page
 */
sample({
  clock: left,
  target: accountSettingsForm.reset,
})

// ============================================================================
// Form Input Handlers
// ============================================================================

/**
 * Handle username input change
 */
sample({
  clock: handleChangeUsername,
  fn: ({ '<event>': event }) => event.target.value,
  target: accountSettingsForm.fields.username.onChange,
})

/**
 * Handle current password input change
 */
sample({
  clock: handleChangeCurrentPassword,
  fn: ({ '<event>': event }) => event.target.value,
  target: accountSettingsForm.fields.currentPassword.onChange,
})

/**
 * Handle new password input change
 */
sample({
  clock: handleChangeNewPassword,
  fn: ({ '<event>': event }) => event.target.value,
  target: accountSettingsForm.fields.newPassword.onChange,
})

/**
 * Handle confirm password input change
 */
sample({
  clock: handleChangeConfirmPassword,
  fn: ({ '<event>': event }) => event.target.value,
  target: accountSettingsForm.fields.confirmPassword.onChange,
})

/**
 * Handle logout sessions checkbox change
 */
sample({
  clock: handleChangeLogoutSessions,
  fn: ({ '<event>': event }) => event.target.checked,
  target: $logoutAfterPasswordChange,
})

/**
 * Handle username input blur
 */
sample({
  clock: handleBlurUsername,
  target: accountSettingsForm.fields.username.onBlur,
})

/**
 * Handle current password input blur
 */
sample({
  clock: handleBlurCurrentPassword,
  target: accountSettingsForm.fields.currentPassword.onBlur,
})

/**
 * Handle new password input blur
 */
sample({
  clock: handleBlurNewPassword,
  target: accountSettingsForm.fields.newPassword.onBlur,
})

/**
 * Handle confirm password input blur
 */
sample({
  clock: handleBlurConfirmPassword,
  target: accountSettingsForm.fields.confirmPassword.onBlur,
})

// ============================================================================
// Submit Logic
// ============================================================================

/**
 * Handle form submission
 */
sample({
  clock: handleSubmit,
  target: accountSettingsForm.submit,
})

/**
 * Submit username change if changed and not empty
 */
sample({
  clock: accountSettingsForm.formValidated,
  source: {
    values: accountSettingsForm.$values,
    initialUsername: $initialUsername,
  },
  filter: ({ values, initialUsername }) =>
    hasUsernameChanged(values.username, initialUsername),
  fn: ({ values }) => ({
    username: values.username.trim(),
  }),
  target: updateUsernameFx,
})

/**
 * Submit password change if all fields filled
 */
sample({
  clock: accountSettingsForm.formValidated,
  source: {
    values: accountSettingsForm.$values,
    logoutAfterPasswordChange: $logoutAfterPasswordChange,
    allPasswordsFilled: $allPasswordsFilled,
  },
  filter: ({ allPasswordsFilled }) => allPasswordsFilled,
  fn: ({ values, logoutAfterPasswordChange }) => ({
    password: values.currentPassword,
    newPassword: values.newPassword,
    logoutAfterPasswordChange,
  }),
  target: updatePasswordFx,
})

/**
 * Track when both username and password updates are requested (same submit)
 */
const setBothUpdatesRequested = createEvent<boolean>()
const $bothUpdatesRequested = createStore(false).on(
  setBothUpdatesRequested,
  (_, value) => value
)

sample({
  clock: accountSettingsForm.formValidated,
  source: {
    values: accountSettingsForm.$values,
    initialUsername: $initialUsername,
    allPasswordsFilled: $allPasswordsFilled,
  },
  fn: ({ values, initialUsername, allPasswordsFilled }) =>
    hasUsernameChanged(values.username, initialUsername) && allPasswordsFilled,
  target: setBothUpdatesRequested,
})

/**
 * Track completion of username/password updates (reset on new submit)
 */
const $usernameUpdateDone = createStore(false)
  .on(updateUsernameFx.done, () => true)
  .reset(accountSettingsForm.formValidated)

const $passwordUpdateDone = createStore(false)
  .on(updatePasswordFx.done, () => true)
  .reset(accountSettingsForm.formValidated)

/**
 * Track failures of username/password updates (reset on new submit)
 */
const $usernameUpdateFailed = createStore(false)
  .on(updateUsernameFx.failData, () => true)
  .reset(accountSettingsForm.formValidated)

const $passwordUpdateFailed = createStore(false)
  .on(updatePasswordFx.failData, () => true)
  .reset(accountSettingsForm.formValidated)

/**
 * Show username_changed_success toast when username was updated successfully.
 * When both requested: show if only username succeeded (password failed).
 * When both succeed → success toast (separate sample).
 */
sample({
  clock: updateUsernameFx.done,
  source: {
    bothRequested: $bothUpdatesRequested,
    passwordFailed: $passwordUpdateFailed,
  },
  filter: ({ bothRequested, passwordFailed }) =>
    !bothRequested || (bothRequested && passwordFailed),
  fn: () => ({
    message: t('username_changed_success'),
    type: NotificationToastType.SUCCESS,
  }),
  target: entitiesNotificationToastsModel.showToast,
})

/**
 * Show password_changed_success toast when password was updated successfully.
 * When both requested: show if only password succeeded (username failed).
 * When both succeed → success toast (separate sample).
 */
sample({
  clock: updatePasswordFx.done,
  source: {
    bothRequested: $bothUpdatesRequested,
    usernameFailed: $usernameUpdateFailed,
  },
  filter: ({ bothRequested, usernameFailed }) =>
    !bothRequested || (bothRequested && usernameFailed),
  fn: () => ({
    message: t('password_changed_success'),
    type: NotificationToastType.SUCCESS,
  }),
  target: entitiesNotificationToastsModel.showToast,
})

/**
 * Show success toast when both username and password were updated successfully
 */
sample({
  clock: [updateUsernameFx.done, updatePasswordFx.done],
  source: {
    bothRequested: $bothUpdatesRequested,
    usernameDone: $usernameUpdateDone,
    passwordDone: $passwordUpdateDone,
  },
  filter: ({ bothRequested, usernameDone, passwordDone }) =>
    bothRequested && usernameDone && passwordDone,
  fn: () => ({
    message: t('success'),
    type: NotificationToastType.SUCCESS,
  }),
  target: entitiesNotificationToastsModel.showToast,
})

/**
 * When both requested and password failed: show username toast (username succeeded)
 */
sample({
  clock: updatePasswordFx.failData,
  source: {
    bothRequested: $bothUpdatesRequested,
    usernameDone: $usernameUpdateDone,
  },
  filter: ({ bothRequested, usernameDone }) => bothRequested && usernameDone,
  fn: () => ({
    message: t('username_changed_success'),
    type: NotificationToastType.SUCCESS,
  }),
  target: entitiesNotificationToastsModel.showToast,
})

/**
 * When both requested and username failed: show password toast (password succeeded)
 */
sample({
  clock: updateUsernameFx.failData,
  source: {
    bothRequested: $bothUpdatesRequested,
    passwordDone: $passwordUpdateDone,
  },
  filter: ({ bothRequested, passwordDone }) => bothRequested && passwordDone,
  fn: () => ({
    message: t('password_changed_success'),
    type: NotificationToastType.SUCCESS,
  }),
  target: entitiesNotificationToastsModel.showToast,
})

// ============================================================================
// Success Handlers
// ============================================================================

/**
 * Reset password fields on successful password change
 */
sample({
  clock: updatePasswordFx.done,
  source: accountSettingsForm.$values,
  fn: (values) => ({
    username: values.username,
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  }),
  target: accountSettingsForm.set,
})

/**
 * Reset logout checkbox after successful password change
 */
sample({
  clock: updatePasswordFx.done,
  fn: () => false,
  target: $logoutAfterPasswordChange,
})

// ============================================================================
// Derived Stores
// ============================================================================

/**
 * Current username value from form
 */
const $usernameValue = accountSettingsForm.$values.map(
  (values) => values.username
)

/**
 * Current password value from form
 */
const $currentPasswordValue = accountSettingsForm.$values.map(
  (values) => values.currentPassword
)

/**
 * New password value from form
 */
const $newPasswordValue = accountSettingsForm.$values.map(
  (values) => values.newPassword
)

/**
 * Confirm password value from form
 */
const $confirmPasswordValue = accountSettingsForm.$values.map(
  (values) => values.confirmPassword
)

/**
 * Username validation error message
 */
const $usernameError = combine(
  {
    error: accountSettingsForm.fields.username.$firstError,
    accountSettingsFormTranslations: $accountSettingsFormTranslations,
  },
  ({ error, accountSettingsFormTranslations: { usernameErrors } }) => {
    if (!error) return ''
    return usernameErrors[error.rule] || ''
  }
)

/**
 * Current password validation error message
 */
const $currentPasswordError = combine(
  accountSettingsForm.fields.currentPassword.$firstError,
  $passwordI18n,
  (firstError, passwordTranslations) => {
    if (!firstError) return ''
    return passwordTranslations(FROM.ACCOUNT)[firstError.rule] || ''
  }
)

/**
 * New password validation error message
 */
const $newPasswordError = combine(
  accountSettingsForm.fields.newPassword.$firstError,
  $passwordI18n,
  (firstError, passwordTranslations) => {
    if (!firstError) return ''
    return passwordTranslations(FROM.ACCOUNT)[firstError.rule] || ''
  }
)

/**
 * Confirm password validation error message
 */
const $confirmPasswordError = combine(
  accountSettingsForm.fields.confirmPassword.$firstError,
  $passwordI18n,
  (firstError, passwordTranslations) => {
    if (!firstError) return ''
    return passwordTranslations(FROM.ACCOUNT)[firstError.rule] || ''
  }
)

/**
 * Loading state for API requests
 */
const $pending = or(updateUsernameFx.pending, updatePasswordFx.pending)

/**
 * Whether submit button should be disabled
 */
const $isSubmitDisabled = combine(
  {
    pending: $pending,
    values: accountSettingsForm.$values,
    initialUsername: $initialUsername,
    isValid: accountSettingsForm.$eachValid,
    allPasswordsFilled: $allPasswordsFilled,
  },
  ({ pending, values, initialUsername, isValid, allPasswordsFilled }) => {
    // Disable if loading
    if (pending) return true

    // Disable if form is invalid
    if (!isValid) return true

    const usernameChanged = hasUsernameChanged(values.username, initialUsername)

    // Disable if nothing changed or if all passwords are not filled
    return !usernameChanged && !allPasswordsFilled
  }
)

// ============================================================================
// Registration
// ============================================================================

// Events
register(handleChangeUsername, 'settings/account/handleChangeUsername')
register(
  handleChangeCurrentPassword,
  'settings/account/handleChangeCurrentPassword'
)
register(handleChangeNewPassword, 'settings/account/handleChangeNewPassword')
register(
  handleChangeConfirmPassword,
  'settings/account/handleChangeConfirmPassword'
)
register(
  handleChangeLogoutSessions,
  'settings/account/handleChangeLogoutSessions'
)
register(handleBlurUsername, 'settings/account/handleBlurUsername')
register(
  handleBlurCurrentPassword,
  'settings/account/handleBlurCurrentPassword'
)
register(handleBlurNewPassword, 'settings/account/handleBlurNewPassword')
register(
  handleBlurConfirmPassword,
  'settings/account/handleBlurConfirmPassword'
)
register(handleSubmit, 'settings/account/changeUsernamePassword')

// Stores
register($usernameValue, '$settings/account/username')
register($currentPasswordValue, '$settings/account/currentPassword')
register($newPasswordValue, '$settings/account/newPassword')
register($confirmPasswordValue, '$settings/account/confirmPassword')
register($usernameError, '$settings/account/usernameError')
register($currentPasswordError, '$settings/account/currentPasswordError')
register($newPasswordError, '$settings/account/newPasswordError')
register($confirmPasswordError, '$settings/account/confirmPasswordError')
register(
  $logoutAfterPasswordChange,
  '$settings/account/logoutAfterPasswordChange'
)
register($isSubmitDisabled, '$settings/account/isSubmitDisabled')
